import { Root as RadixRoot } from '@radix-ui/react-dropdown-menu';
import {
  Children,
  cloneElement,
  isValidElement,
  type ReactElement,
  type ReactNode,
  useCallback,
  useMemo,
  useState,
} from 'react';

import { COMPONENT_NAME } from './constants.js';
import { Content, type MenuContentProps } from './Content.js';
import { MenuContext } from './contexts/MenuContext.js';
import { MobileMenuModeContext } from './contexts/MobileMenuModeContext.js';
import {
  MobileMenuNavigationContext,
  type MobileMenuNavigationLevel,
} from './contexts/MobileMenuNavigationContext.js';
import { Group } from './Group.js';
import { useMediaQuery } from './hooks/useMediaQuery.js';
import { useMenuAutoClose } from './hooks/useMenuAutoClose.js';
import { Intent } from './Intent.js';
import { Item } from './Item.js';
import { ItemTooltip } from './ItemTooltip.js';
import { Label } from './Label.js';
import { Link } from './Link.js';
import { Prefix } from './Prefix.js';
import { Sub } from './Sub.js';
import { SubContent } from './SubContent.js';
import { SubTrigger } from './SubTrigger.js';
import { Suffix } from './Suffix.js';
import { Trigger } from './Trigger.js';
import { useControllableState } from '../../core/hooks/useControllableState.js';
import { useId } from '../../core/hooks/useId.js';
import { DataTestId } from '../../core/types/data-props.js';
import { MaskingProps } from '../../core/types/masking-props.js';
import { WithChildren } from '../../core/types/with-children.js';
import { isSlot } from '../../core/utils/is-slot.js';
import { mergeProps as _mergeProps } from '../../core/utils/merge-props.js';

// TODO Remove logic for handling tab key as soon as the related bug in the radix-ui
// library has been fixed and we've upgraded to a newer version that contains the fix
// (see https://github.com/radix-ui/primitives/issues/1565 and https://github.com/radix-ui/primitives/pull/1576)

const DEFAULT_MOBILE_BREAKPOINT = 480;

/** Used to derive the mobile sheet's root title from the trigger's children. */
function extractTextContent(node: ReactNode): string {
  if (typeof node === 'string' || typeof node === 'number') {
    return String(node);
  }
  if (Array.isArray(node)) {
    return node.map(extractTextContent).join('');
  }
  if (isValidElement(node)) {
    return extractTextContent(
      (node.props as Record<string, ReactNode>).children,
    );
  }
  return '';
}

/**
 * @public
 */
export interface MenuProps extends WithChildren, DataTestId, MaskingProps {
  /**
   * The open state of the dropdown menu when it is initially rendered. Use when
   * you do not need to control its open state.
   * @defaultValue false
   */
  defaultOpen?: boolean;
  /**
   * The controlled open state of the dropdown menu. Must be used in conjunction
   * with onOpenChange.
   */
  open?: boolean;
  /**
   * Event handler called when the open state of the dropdown menu changes.
   * @param isOpen - true if the menu has been opened
   */
  onOpenChange?: (isOpen: boolean) => void;
  /**
   * The modality of the dialog.
   * When set to true, interaction with outside elements will be disabled and
   * only dialog content will be visible to screen readers.
   * @defaultValue true
   */
  modal?: boolean;
  /**
   * The maximum viewport width in pixels at which the mobile drill-down menu
   * is rendered instead of the standard dropdown.
   * @defaultValue 480
   */
  mobileBreakpoint?: number;
}

type MenuSlots = {
  trigger: ReactNode | null;
  content: ReactNode[];
  freeform: ReactNode[];
};

/** Iterates over child nodes to extract specific component slots. */
function getMenuSlots(children: ReactNode) {
  const slots: MenuSlots = {
    trigger: null,
    content: [],
    freeform: [],
  };

  Children.forEach(children, (child) => {
    if (isSlot(child, Trigger)) {
      slots.trigger = child;
    } else if (isSlot(child, Content)) {
      slots.content.push(child);
    } else {
      slots.freeform.push(child);
    }
  });

  return slots;
}

/** Iterates over all top-level Content nodes and registers tab key handler. */
function registerTabHandlers(
  children: ReactNode,
  handleTabKeyPressed: () => void,
  dataDtComponent: string,
): ReactNode {
  return Children.map(children, (child: ReactNode) => {
    return isValidElement<MenuContentProps>(child) && child.type === Content
      ? cloneElement(
          child,
          _mergeProps(child.props, {
            _onTabKeyPressed: handleTabKeyPressed,
            'data-dt-component': dataDtComponent,
          }),
          child.props.children,
        )
      : child;
  });
}

/**
 * @internal
 */
const MenuRoot = (props: MenuProps) => {
  const {
    open: value,
    onOpenChange: onChange,
    modal = true,
    defaultOpen = false,
    children,
    mobileBreakpoint = DEFAULT_MOBILE_BREAKPOINT,
    'data-testid': dataTestId,
    'data-dtrum-mask': dataDtrumMask,
    'data-dtrum-allow': dataDtrumAllow,
    // @ts-expect-error - We want to allow overriding data-dt-component as we use the component internally in other Strato components, but it should not appear in the public API.
    'data-dt-component': dataDtComponent = COMPONENT_NAME,
  } = props;

  const contentId = useId();

  const [open, updateOpen] = useControllableState({
    defaultValue: defaultOpen,
    value,
    onChange,
  });

  const slots = useMemo(
    () =>
      getMenuSlots(
        registerTabHandlers(children, () => updateOpen(false), dataDtComponent),
      ),
    [children, updateOpen, dataDtComponent],
  );

  useMenuAutoClose(open, updateOpen);

  const menuContextValue = useMemo(
    () => ({
      isOpen: open,
      isDefaultOpen: defaultOpen,
      setIsOpen: updateOpen,
      contentId,
    }),
    [defaultOpen, open, updateOpen, contentId],
  );

  const isMobile = useMediaQuery(`(max-width: ${mobileBreakpoint}px)`);

  const [navStack, setNavStack] = useState<MobileMenuNavigationLevel[]>([]);
  const [navDirection, setNavDirection] = useState<'forward' | 'back'>(
    'forward',
  );
  const [prevOpen, setPrevOpen] = useState(open);

  // Reset navigation state when the menu closes (derived-state pattern).
  if (prevOpen !== open) {
    setPrevOpen(open);
    if (!open) {
      setNavStack([]);
      setNavDirection('forward');
    }
  }

  const push = useCallback((level: MobileMenuNavigationLevel) => {
    setNavDirection('forward');
    setNavStack((prev) => [...prev, level]);
  }, []);

  const pop = useCallback(() => {
    setNavDirection('back');
    setNavStack((prev) => prev.slice(0, -1));
  }, []);

  const rootTitle = useMemo(() => {
    if (!slots.trigger) {
      return '';
    }
    return extractTextContent(
      (slots.trigger as ReactElement<{ children?: ReactNode }>).props
        .children as ReactNode,
    );
  }, [slots.trigger]);

  const mobileNavContextValue = useMemo(
    () => ({ stack: navStack, direction: navDirection, rootTitle, push, pop }),
    [navStack, navDirection, rootTitle, push, pop],
  );

  return (
    <MobileMenuNavigationContext.Provider value={mobileNavContextValue}>
      <MobileMenuModeContext.Provider value={isMobile}>
        <MenuContext.Provider value={menuContextValue}>
          <RadixRoot
            open={open}
            onOpenChange={(isOpen) => updateOpen(isOpen)}
            modal={modal}
            data-testid={dataTestId}
            data-dtrum-mask={dataDtrumMask}
            data-dtrum-allow={dataDtrumAllow}
          >
            {slots.trigger !== null && slots.trigger}
            {open && slots.content}
            {slots.freeform}
          </RadixRoot>
        </MenuContext.Provider>
      </MobileMenuModeContext.Provider>
    </MobileMenuNavigationContext.Provider>
  );
};

(MenuRoot as typeof MenuRoot & { displayName: string }).displayName = 'Menu';

/**
 * The `Menu` component displays the list of action items that are revealed when
 * the user clicks on the trigger element.
 * @public
 */
export const Menu = Object.assign(MenuRoot, {
  Trigger,
  Content,
  Item,
  ItemTooltip,
  Label,
  Link,
  Group,
  Prefix,
  Suffix,
  Sub,
  SubTrigger,
  SubContent,
  Intent,
});
